import { EduleadClient } from 'edulead'
import { fetchAuthSession } from 'aws-amplify/auth'
import { useApiActivityStore } from '../zustand/useApiActivityStore'

/**
 * Client for the Go API's public surface (/v4), generated by Fern from the API definition in
 * github.com/getedulead/edulead and consumed here as the `edulead` package (specs/go-migration
 * main-frontend.md and sdk.md there).
 *
 * - Base URL: VITE_EDULEAD_API_URL when set; otherwise the local Go API's /v4 on :8080 when the
 *   app runs on localhost (`make api-local` in the Go repo), production otherwise. Locally the
 *   prefix names the surface, as API Gateway's header does in production: a path /v4 and
 *   /internal/v1 both hold (GET /institutions/{id}) would otherwise reach the internal one.
 * - Auth: the Cognito ID token of the signed-in user, fetched per request so a refreshed session
 *   is picked up; the SDK adds the `Bearer` prefix. The ID token, not the access token, because
 *   only it carries the `profile` attribute the API reads the OWNER role from.
 * - Staff: the API treats users of the staff team as staff, who must name the team they act for
 *   in `Edulead-Team`. In this app staff act for their own team, so the header carries it; for
 *   everyone else it is absent (the API refuses it from a non-staff caller).
 * - Activity: every request is counted in flight until its response arrives
 *   (useApiActivityStore), so a page can show a spinner while it waits.
 */
const isLocalhost = ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname)

const environment =
  import.meta.env.VITE_EDULEAD_API_URL ??
  (isLocalhost ? 'http://localhost:8080/v4' : 'https://api.getedulead.com/v4')

/** The Go API's staff team (EDULEAD_STAFF_TEAM_ID there), one of the legacy admin teams. */
const STAFF_TEAM_ID = '62948182-2201-4c73-952c-35d56474c039'

/** The team of the signed-in user's ID token (its one Cognito group), or undefined. */
async function teamOf(): Promise<string | undefined> {
  const session = await fetchAuthSession()
  const groups = session.tokens?.idToken?.payload['cognito:groups']
  return Array.isArray(groups) && typeof groups[0] === 'string' ? groups[0] : undefined
}

/** fetch, counting the request in flight until its response (or failure) arrives. */
const trackedFetch: typeof fetch = async (input, init) => {
  const { begin, end } = useApiActivityStore.getState()
  begin()
  try {
    return await fetch(input, init)
  } finally {
    end()
  }
}

export const eduleadV4 = new EduleadClient({
  environment,
  fetch: trackedFetch,
  token: async () => {
    const session = await fetchAuthSession()
    return session.tokens?.idToken?.toString() ?? ''
  },
  headers: {
    'Edulead-Team': async () => {
      const team = await teamOf()
      return team === STAFF_TEAM_ID ? team : null
    },
  },
})
